import React, { useEffect, Fragment } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";
import { DN0 } from "@atlaskit/theme/colors";

import { Adg4Button } from "@/components/adg4/button/src";
import { AisSitesType } from "@/libraries/bxp-web-stargate-client/src/index";
import { Link } from "@/libraries/design/components";
import { DEFAULT_VALUES, useLocalizedHref } from "@/libraries/web-framework";
import { Logo } from "@/libraries/wpl/foundations/src";
import {
    EnhancedBannerAlert,
    EnhancedBannerProps
} from "@/patterns/adg4/enhanced-banner-alert/enhanced-banner-alert";
import { experiments, useExperiment } from "@/utils/experiment";

import { useAnalyticsClient } from "../../../../../../../analytics/wac-analytics/analytics-client.tsx";

import { FlareSvg } from "./flare-svg.tsx";

const START_URL =
    process.env["NODE_ENV"] === "production"
        ? "https://start.atlassian.com"
        : "https://start.stg.atlassian.com";
const SIGNUP_URL =
    process.env["NODE_ENV"] === "production"
        ? "https://atlassian.com/try/cloud/signup?bundle=confluence&edition=free"
        : "https://wac.stg.internal.atlassian.com/try/cloud/signup?bundle=confluence&edition=free";

const sectionCSS = css({
    [`&.lifted`]: {
        position: "relative"
    }
});

const wrapLogMeSecAndLines = css({
    backgroundImage:
        "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTQ0MCIgaGVpZ2h0PSI2ODUiIHZpZXdCb3g9IjAgMCAxNDQwIDY4NSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMCAwSDE0NDBWNjUyLjg5MUwwIDY4NVYwWiIgZmlsbD0iI0U5RjJGRSIvPjwvc3ZnPg==')",
    backgroundSize: "cover",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8%",
    margin: "0 auto",
    padding: "3rem 5rem 7.5rem",
    [media.above.md]: {
        height: "685px"
    },
    [media.below.md]: {
        flexDirection: "column",
        gap: 48
    },
    [`&.lifted`]: {
        paddingBlockEnd: "275px"
    }
});

// Adjustments for fixed top nav.
// The logged in paid pages have a position:fixed top nav.
const wrapLogMeSecAndLinesLoggedInPaid = css({
    [media.above.md]: {
        height: "auto"
    },
    "@media (min-width: 600px)": {
        padding: "108px 80px 80px"
    },
    "@media (max-width: 600px)": {
        padding: "96px 24px 80px"
    }
});

const logMeBackInSectionStyle = css({
    padding: "24px 0px 24px 0px",
    [media.above.sm]: {
        flexDirection: "row"
    },
    [media.above.md]: {
        maxWidth: "468px"
    },
    [media.below.md]: {
        width: "100%"
    }
});

const headingStyle = css({
    fontSize: "48px",
    lineHeight: "56px",
    fontWeight: "500",
    marginBlockStart: "0px",
    marginBlockEnd: "8px"
});

const subHeadingStyle = css({
    fontSize: "20px",
    marginBlockEnd: "24px",
    marginBlockStart: "0px"
});

const confluenceSiteBoxContainerStyle = css({
    position: "relative"
});

const flareContainerCss = css({
    "position": "absolute",
    "insetInlineStart": -64,
    "zIndex": 2,
    "@media (max-width: 600px)": {
        display: "none"
    }
});

const confluenceSiteBoxUnderlayStyle = css({
    "width": "80%",
    "position": "absolute",
    "insetBlockEnd": -18,
    "insetInlineEnd": -14,
    "background": "#FCA700",
    "height": 60,
    "@media (max-width: 600px)": {
        height: 104
    }
});

const confluenceSiteBoxStyle = css({
    "position": "relative",
    "zIndex": 1,
    "width": "auto",
    "height": "72px",
    "display": "flex",
    "justifyContent": "space-between",
    "flexDirection": "row",
    "alignItems": "center",
    "border": `solid 1px ${DN0}`,
    "borderRadius": 4,
    "padding": "16px 24px 16px 16px",
    "backgroundColor": "#ffff",
    "@media (max-width: 600px)": {
        flexDirection: "column",
        height: "auto",
        alignItems: "start",
        padding: "7px 16px 16px"
    }
});

const logoAndSiteNameStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minWidth: "0px"
});

const logoStyle = css({
    marginInlineEnd: "10px",
    marginBlockStart: "9px"
});

const siteStyle = css({
    lineHeight: "22px",
    fontSize: "16px",
    overflow: "hidden"
});

const productStyle = css({
    fontWeight: "500",
    height: "22px",
    fontSize: "17px",
    textAlign: "start"
});

const siteNameStyle = css({
    fontWeight: "300",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    height: "22px"
});

const otherSiteLinkStyle = css({
    marginBlockStart: "48px"
});

const buttonStyle = css({
    "whiteSpace": "nowrap",
    "marginInlineStart": "15px",
    "@media (max-width: 600px)": {
        marginInlineStart: 0,
        width: "100%",
        maxWidth: "100%"
    }
});

const imgWrapperStyle = css({
    [media.above.md]: {
        maxWidth: "650px"
    },
    [media.below.md]: {
        width: "100%"
    }
});

const imgStyle = css({
    width: "100%"
});

enum TRACKING_EVENT_NAMES {
    loggedInHeroProductCtaClicked = "loggedInHeroProductCtaClicked",
    loggedInHeroAnotherSiteCtaClicked = "loggedInHeroAnotherSiteCtaClicked",
    loggedInHeroNewSiteCtaClicked = "loggedInHeroNewSiteCtaClicked"
}

interface LogMeInSectionProps {
    confluenceSites: AisSitesType[];
    userProfileName: string | undefined;
    imgSrc?: string;
    loggedInPaid?: boolean;
}

// eslint-disable-next-line complexity
export const LogMeInSection = ({
    confluenceSites,
    userProfileName,
    // eslint-disable-next-line local/no-non-orangelogic-images-eslint
    imgSrc = "https://wac-cdn.atlassian.com/misc-assets/webp-images/Hero Image B-1884788674.webp",
    loggedInPaid
}: LogMeInSectionProps) => {
    const createConfluenceUrl = (url: string) => {
        try {
            const tempUrl = new URL(url);
            tempUrl.pathname = "/wiki";
            tempUrl.search = "";
            return tempUrl.toString();
        } catch {
            return url;
        }
    };

    const getSitenameFromUrl = (url: string) => {
        try {
            const tempUrl = new URL(url);
            return tempUrl.hostname.split(".")[0] || "";
        } catch {
            return "";
        }
    };

    const firstName = userProfileName?.split(" ")[0];
    const siteUrl =
        createConfluenceUrl(confluenceSites[0]?.url || "") || START_URL;
    const siteName = getSitenameFromUrl(confluenceSites[0]?.url || "");
    const hasMultipleConfluences = confluenceSites.length > 1;

    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    useEffect(() => {
        if (typeof window !== "undefined") {
            createAnalyticsEvent({
                eventType: "ui",
                action: "loaded",
                actionSubject: "page",
                actionSubjectId: "loggedInHero",
                source: "/software/confluence",
                attributes: {
                    url: window.location.href
                }
            }).fire();
        }
    }, [analyticsClient, createAnalyticsEvent]);

    const clickHandler = (eventName: TRACKING_EVENT_NAMES) => {
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "loggedInHero",
            source: "/software/confluence",
            attributes: {
                eventName,
                url: window.location.href
            }
        }).fire();
    };

    const intl = useIntl();

    const linkUrl = loggedInPaid ? SIGNUP_URL : START_URL;

    const signupHref = useLocalizedHref(
        "/try/cloud/signup?bundle=confluence&edition=free&createNew=true"
    );

    const gwp5090BannerExperiment = useExperiment(
        experiments.gwp5090ExperimentBannerConfluence
    );

    const gwp5090IsLiftedBanner =
        gwp5090BannerExperiment != undefined &&
        gwp5090BannerExperiment.type === "enrolled" &&
        gwp5090BannerExperiment.value === "lifted";

    const gwp5090Team25Config: EnhancedBannerProps = {
        identifier: "GWP5090.confluence-product-tour.lifted",
        customCSS: {
            position: "absolute",
            margin: "auto",
            inset: "auto 0 90px"
        },
        bannerType: "lifted",
        text: (
            <>
                <FormattedMessage
                    id="team25-banners-and-butterbars.confluence.text"
                    defaultMessage="<strong>April 8-10 • Anaheim, California</strong>Hear from the world&rsquo;s leading product experts on how to boost team performance at Team &rsquo;25!"
                    values={DEFAULT_VALUES}
                />
            </>
        ),
        mobileText: (
            <>
                <FormattedMessage
                    id="team25-banners-and-butterbars.confluence.mobile-text"
                    defaultMessage="<strong>April 8-10 • Anaheim, California</strong>Hear from leading product experts!"
                    values={DEFAULT_VALUES}
                />
            </>
        ),
        productKey: "home",
        backgroundPreset: "gwp-5090-banners",
        imageProps: {
            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/gf1wkse40oux4nf58b103kknvb18p4yc.webp",
            alt: "Team25", //Event name is not localized
            width: 200
        },
        linkText: intl.formatMessage({
            id: "team25-banners-and-butterbars.confluence.cta",
            defaultMessage: "Get your pass"
        }),
        linkUrl:
            "https://events.atlassian.com/team?utm_medium=event-web-referral&utm_source=confluence_product-tour&event_context=701QB00000Lg6oBYAR&utm_campaign=K:event|H:any|N:Team_25|P:agnostic|O:event-marketing|F:pre-event|C:event-home&utm_content=lifted-bar_WEBTEAM-17396&event_id=216653",
        dataEventProps: {
            "data-product-key": "home",
            "data-event-component": "linkButton",
            "data-event-container": "announcementBanner",
            "data-event": "clicked",
            "data-label": intl.formatMessage({
                id: "team25-banners-and-butterbars.confluence.cta",
                defaultMessage: "Get your pass"
            }),
            "data-label-english": "Get your pass"
        }
    };

    const classWrapperName = gwp5090IsLiftedBanner ? "lifted" : "standard";

    return (
        <Fragment>
            <section css={sectionCSS} className={classWrapperName}>
                <div
                    className={classWrapperName}
                    css={[
                        wrapLogMeSecAndLines,
                        loggedInPaid && wrapLogMeSecAndLinesLoggedInPaid
                    ]}
                >
                    <div css={logMeBackInSectionStyle}>
                        <h1 css={headingStyle}>
                            {firstName ? (
                                <FormattedMessage
                                    id="ConfluenceSoftwarePageTemplate.loggedInHero.welcomeBackWithName"
                                    defaultMessage={`Welcome back, {name}.`}
                                    values={{ name: firstName }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="ConfluenceSoftwarePageTemplate.loggedInHero.welcomeBack"
                                    defaultMessage="Welcome back"
                                />
                            )}
                        </h1>
                        <p css={subHeadingStyle}>
                            <FormattedMessage
                                id="ConfluenceSoftwarePageTemplate.loggedInHero.pickUpWhereYouLeftOff"
                                defaultMessage="Pick up where you left off in Confluence."
                            />
                        </p>

                        <div css={confluenceSiteBoxContainerStyle}>
                            <div css={flareContainerCss}>
                                <FlareSvg />
                            </div>
                            <div css={confluenceSiteBoxStyle}>
                                <div css={logoAndSiteNameStyle}>
                                    <div css={logoStyle}>
                                        <Logo
                                            brand="confluence"
                                            type="mark"
                                            appearance="brand"
                                        />
                                    </div>
                                    <div css={siteStyle}>
                                        <div css={productStyle}>Confluence</div>
                                        <div css={siteNameStyle}>
                                            {siteName}
                                        </div>
                                    </div>
                                </div>
                                <Adg4Button
                                    id="confluence-button"
                                    variant="primary"
                                    size="small"
                                    color="blue"
                                    css={buttonStyle}
                                    href={siteUrl}
                                    onClick={() =>
                                        clickHandler(
                                            TRACKING_EVENT_NAMES.loggedInHeroProductCtaClicked
                                        )
                                    }
                                >
                                    <FormattedMessage
                                        id="ConfluenceSoftwarePageTemplate.loggedInHero.goToConfluence"
                                        defaultMessage="Go to Confluence"
                                    />
                                </Adg4Button>
                            </div>
                            <div css={confluenceSiteBoxUnderlayStyle} />
                        </div>

                        {hasMultipleConfluences && (
                            <Link
                                href={linkUrl}
                                text={intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.loggedInHero.lookingForDifferentSite",
                                    defaultMessage:
                                        "Looking for a different site?"
                                })}
                                onClick={() =>
                                    clickHandler(
                                        TRACKING_EVENT_NAMES.loggedInHeroAnotherSiteCtaClicked
                                    )
                                }
                                css={otherSiteLinkStyle}
                            />
                        )}

                        {loggedInPaid && (
                            <Link
                                href={signupHref}
                                text={intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.loggedInHero.orCreateANewWorkspace",
                                    defaultMessage: "Or create a new workspace"
                                })}
                                onClick={() =>
                                    clickHandler(
                                        TRACKING_EVENT_NAMES.loggedInHeroNewSiteCtaClicked
                                    )
                                }
                                css={otherSiteLinkStyle}
                            />
                        )}
                    </div>
                    <div css={imgWrapperStyle}>
                        {/* eslint-disable-next-line no-restricted-syntax */}
                        <img alt="" src={imgSrc} css={imgStyle} />
                    </div>
                </div>
                {gwp5090IsLiftedBanner && (
                    <EnhancedBannerAlert {...gwp5090Team25Config} />
                )}
            </section>
        </Fragment>
    );
};

export default LogMeInSection;
